import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  BanknotesIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { depenseService } from '../../services/api';
import {
  DepenseList as DepenseListType,
  Depense,
  CreateDepenseData,
  CategorieDepense,
  StatutDepense,
  PayerDepenseData,
} from '../../types';
import DepenseForm from './DepenseForm';
import PayerDepenseModal from './PayerDepenseModal';

const DepenseListComponent: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategorie, setSelectedCategorie] = useState<string>('');
  const [selectedStatut, setSelectedStatut] = useState<string>('');
  const [selectedNature, setSelectedNature] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingDepense, setEditingDepense] = useState<Depense | null>(null);
  const [showPayerModal, setShowPayerModal] = useState(false);
  const [payingDepense, setPayingDepense] = useState<Depense | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch depenses
  const { data: depensesData, isLoading } = useQuery({
    queryKey: ['depenses', currentPage, searchTerm, selectedCategorie, selectedStatut, selectedNature],
    queryFn: () =>
      depenseService.getDepenses({
        page: currentPage,
        search: searchTerm || undefined,
        categorie: selectedCategorie || undefined,
        statut: selectedStatut || undefined,
        nature: selectedNature || undefined,
      }),
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-depenses'],
    queryFn: () => depenseService.getCategories({ page_size: 100 }),
  });

  // Fetch statuts
  const { data: statutsData } = useQuery({
    queryKey: ['statuts-depenses'],
    queryFn: () => depenseService.getStatuts(),
  });

  const depenses = depensesData?.data?.results || [];
  const totalCount = depensesData?.data?.count || 0;
  const categories = categoriesData?.data?.results || [];
  const statuts = statutsData?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateDepenseData) => depenseService.createDepense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['depenses'] });
      queryClient.invalidateQueries({ queryKey: ['depenses-statistiques'] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateDepenseData> }) =>
      depenseService.updateDepense(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['depenses'] });
      queryClient.invalidateQueries({ queryKey: ['depenses-statistiques'] });
      setEditingDepense(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => depenseService.deleteDepense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['depenses'] });
      queryClient.invalidateQueries({ queryKey: ['depenses-statistiques'] });
    },
  });

  const approuverMutation = useMutation({
    mutationFn: (id: number) => depenseService.approuverDepense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['depenses'] });
      queryClient.invalidateQueries({ queryKey: ['depenses-statistiques'] });
    },
  });

  const payerMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PayerDepenseData }) =>
      depenseService.payerDepense(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['depenses'] });
      queryClient.invalidateQueries({ queryKey: ['depenses-statistiques'] });
      setShowPayerModal(false);
      setPayingDepense(null);
    },
  });

  const annulerMutation = useMutation({
    mutationFn: (id: number) => depenseService.annulerDepense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['depenses'] });
      queryClient.invalidateQueries({ queryKey: ['depenses-statistiques'] });
    },
  });

  const handleSubmit = (data: CreateDepenseData) => {
    if (editingDepense) {
      updateMutation.mutate({ id: editingDepense.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = async (depense: DepenseListType) => {
    // Fetch full depense data
    const response = await depenseService.getDepense(depense.id);
    setEditingDepense(response.data);
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette dépense ?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleApprouver = (id: number) => {
    approuverMutation.mutate(id);
  };

  const handlePayer = async (depense: DepenseListType) => {
    const response = await depenseService.getDepense(depense.id);
    setPayingDepense(response.data);
    setShowPayerModal(true);
  };

  const handlePayerSubmit = (data: PayerDepenseData) => {
    if (payingDepense) {
      payerMutation.mutate({ id: payingDepense.id, data });
    }
  };

  const handleAnnuler = (id: number) => {
    if (window.confirm('Voulez-vous vraiment annuler cette dépense ?')) {
      annulerMutation.mutate(id);
    }
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };

  const getStatutBadge = (statut: StatutDepense) => {
    const badges: Record<StatutDepense, { bg: string; text: string; label: string }> = {
      en_attente: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      approuvee: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Approuvée' },
      payee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Payée' },
      annulee: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Annulée' },
    };
    const badge = badges[statut] || badges.en_attente;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPrioriteBadge = (priorite: string) => {
    const badges: Record<string, { bg: string; text: string }> = {
      basse: { bg: 'bg-gray-100', text: 'text-gray-600' },
      normale: { bg: 'bg-blue-100', text: 'text-blue-600' },
      haute: { bg: 'bg-orange-100', text: 'text-orange-600' },
      urgente: { bg: 'bg-red-100', text: 'text-red-600' },
    };
    const badge = badges[priorite] || badges.normale;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
        {priorite.charAt(0).toUpperCase() + priorite.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Dépenses</h1>
        <button
          onClick={() => {
            setEditingDepense(null);
            setShowForm(true);
          }}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nouvelle dépense
        </button>
      </div>

      {/* Filters */}
      <div className="son-panel p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-700"
            />
          </div>

          {/* Catégorie filter */}
          <select
            value={selectedCategorie}
            onChange={(e) => setSelectedCategorie(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-700"
          >
            <option value="">Toutes les catégories</option>
            {/* Le filtre conserve les catégories désactivées : elles restent
                nécessaires pour retrouver les dépenses historiques. */}
            {categories.map((cat: CategorieDepense) => (
              <option key={cat.id} value={cat.id}>
                {cat.nom}{cat.is_active ? '' : ' (inactive)'}
              </option>
            ))}
          </select>

          {/* Statut filter */}
          <select
            value={selectedStatut}
            onChange={(e) => setSelectedStatut(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-700"
          >
            <option value="">Tous les statuts</option>
            {statuts.map((s: { value: string; label: string }) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          {/* Nature filter */}
          <select
            value={selectedNature}
            onChange={(e) => setSelectedNature(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-700"
          >
            <option value="">Toutes natures</option>
            <option value="journaliere">Journalières (caisse)</option>
            <option value="mensuelle">Mensuelles (hors caisse)</option>
          </select>

          {/* Reset */}
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategorie('');
              setSelectedStatut('');
              setSelectedNature('');
            }}
            className="flex items-center justify-center px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="son-table-wrap overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : depenses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucune dépense trouvée</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  N° / Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priorité
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {depenses.map((depense: DepenseListType) => (
                <tr key={depense.id} className={depense.est_en_retard ? 'bg-red-50' : ''}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{depense.numero}</div>
                    <div className="text-sm text-gray-500">{depense.description}</div>
                    {depense.fournisseur_nom && (
                      <div className="text-xs text-gray-400">{depense.fournisseur_nom}</div>
                    )}
                    {(depense.nature === 'mensuelle' || !depense.impacte_caisse) && (
                      <span
                        title={
                          depense.impacte_caisse
                            ? 'Dépense mensuelle, décaissée de la caisse du jour'
                            : "N'apparaît pas dans le folio de caisse du jour"
                        }
                        className="inline-block mt-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-indigo-50 text-indigo-700"
                      >
                        {depense.nature === 'mensuelle' ? 'Mensuelle' : 'Journalière'}
                        {!depense.impacte_caisse && ' · hors caisse'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="px-2 py-1 text-xs font-medium rounded-full"
                      style={{
                        backgroundColor: `${depense.categorie_couleur}20`,
                        color: depense.categorie_couleur,
                      }}
                    >
                      {depense.categorie_nom}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatMontant(depense.montant)} GNF
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(depense.date_depense).toLocaleDateString('fr-FR')}
                    </div>
                    {depense.date_echeance && (
                      <div className={`text-xs ${depense.est_en_retard ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        {depense.est_en_retard && <ExclamationTriangleIcon className="h-3 w-3 inline mr-1" />}
                        Éch: {new Date(depense.date_echeance).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatutBadge(depense.statut)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPrioriteBadge(depense.priorite)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {depense.statut === 'en_attente' && (
                        <>
                          <button
                            onClick={() => handleApprouver(depense.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Approuver"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEdit(depense)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Modifier"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {(depense.statut === 'en_attente' || depense.statut === 'approuvee') && (
                        <>
                          <button
                            onClick={() => handlePayer(depense)}
                            className="text-green-600 hover:text-green-900"
                            title="Payer"
                          >
                            <BanknotesIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleAnnuler(depense.id)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Annuler"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {depense.statut !== 'payee' && depense.statut !== 'annulee' && (
                        <button
                          onClick={() => handleDelete(depense.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalCount > 20 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              {totalCount} dépenses au total
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="px-3 py-1">
                Page {currentPage} / {Math.ceil(totalCount / 20)}
              </span>
              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage >= Math.ceil(totalCount / 20)}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <DepenseForm
          depense={editingDepense}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingDepense(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Payer Modal */}
      {showPayerModal && payingDepense && (
        <PayerDepenseModal
          depense={payingDepense}
          onSubmit={handlePayerSubmit}
          onClose={() => {
            setShowPayerModal(false);
            setPayingDepense(null);
          }}
          isLoading={payerMutation.isPending}
        />
      )}
    </div>
  );
};

export default DepenseListComponent;
